import { GoodVersion } from "@/entities/goodVersion";
import { sanitizeGoodVersionForApi } from "@/lib/goodsApiPayload";
import { useGoodsStore } from "@/stores/goods";
import { geniFetch } from "./geniBackend";
import { patchGoodImages } from "./saveGood";
import { imageRefNeedsCrmUpload, uploadImageToCrm } from "./saveImage";
import { normalizeGoodImageList } from "@/utils/goodVersionDisplay";
import { normalizeStoredImageRef } from "@/utils/imageRef";

async function ensureVersionImagesStoredOnServer(version: GoodVersion): Promise<void> {
  const list = version.media?.images;
  if (!Array.isArray(list) || list.length === 0) return;
  version.media.images = await Promise.all(
    list.map(async (img) => {
      const s = typeof img === "string" ? img.trim() : String(img).trim();
      if (!s) return s;
      if (imageRefNeedsCrmUpload(s)) {
        return await uploadImageToCrm(s);
      }
      return normalizeStoredImageRef(s);
    })
  );
}

export const saveGoodVersion = async (version: GoodVersion, goodId: string) => {
  const apiVersion = sanitizeGoodVersionForApi(version);
  await ensureVersionImagesStoredOnServer(apiVersion);

  const goodStore = useGoodsStore();

  let good = goodStore.goods.find((g) => g.id === goodId);
  if (!good) {
    await goodStore.refresh({ silent: true });
    good = goodStore.goods.find((g) => g.id === goodId);
  }

  if (!good) {
    throw new Error(`Good ${goodId} not found in store`);
  }

  if (!good.versions) {
    good.versions = [];
  }
  const existedVersion = good.versions.find((v) => v.id === version.id);

  const storedImages = normalizeGoodImageList(apiVersion.media?.images);

  if (existedVersion?.profile_id) {
    await geniFetch(`/goods/${goodId}`, {
      method: "PATCH",
      body: JSON.stringify({ metadata: { latestVersion: apiVersion } }),
    });
    if (storedImages.length > 0) {
      await patchGoodImages(goodId, storedImages);
    }

    return existedVersion.profile_id;
  } else {
    const response = await geniFetch<{ id: string }>(`/goods/${goodId}/versions`, {
      method: "POST",
      body: JSON.stringify({
        title: apiVersion.title,
        payload: apiVersion,
      }),
    });

    if (storedImages.length > 0) {
      await patchGoodImages(goodId, storedImages);
    }

    return response.id as unknown as number;
  }
};
